import { useState, type FormEvent } from 'react';
import { contactServiceOptions, propertyTypes } from '@/mocks/contact';

const FORM_URL = 'https://readdy.ai/api/form/db88kdk6oi3g3tv8ujug';
const inputCls = 'w-full h-[52px] rounded-xl border border-background-300 bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200';
type Status = 'idle' | 'sending' | 'success' | 'error';

export default function QuoteForm({ defaultService = '' }: { defaultService?: string }) {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [count, setCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    if (String(data.get('company_alt') || '').trim()) { setStatus('success'); return; }
    data.delete('company_alt');

    const next: Record<string, string> = {};
    if (!String(data.get('full_name') || '').trim()) next.full_name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(data.get('email') || '').trim())) next.email = 'Please enter a valid email address.';
    if (!data.get('service_type')) next.service_type = 'Please choose a service type.';
    if (String(data.get('details') || '').length > 500) next.details = 'Please keep details under 500 characters.';
    setErrors(next);
    if (Object.keys(next).length) {
      const first = form.querySelector<HTMLElement>(`[name="${Object.keys(next)[0]}"]`);
      first?.focus();
      return;
    }

    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => { if (String(v).trim()) body.append(k, String(v)); });
      const response = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('success');
        form.reset();
        setCount(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Your request could not be sent. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error — your request was not sent. Please check your connection and try again.');
    }
  };

  const field = (k: string) => ({ 'aria-invalid': Boolean(errors[k]), 'aria-describedby': errors[k] ? `${k}-err` : undefined });
  const err = (k: string) => errors[k] && <p id={`${k}-err`} className="mt-1.5 text-xs text-foreground-950"><i className="ri-error-warning-line text-secondary-500 mr-1" />{errors[k]}</p>;
  const opt = <span className="text-foreground-500 font-normal">(optional)</span>;

  return (
    <form id="cleary-contact-quote-form" data-readdy-form noValidate onSubmit={handleSubmit} className="quote-form relative rounded-[20px] bg-background-50 border border-background-300 p-6 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
      <input type="text" name="company_alt" className="field-company-alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      <div>
        <label htmlFor="c_full_name" className="block text-sm font-medium mb-2">Full Name <span className="text-secondary-500">*</span></label>
        <input id="c_full_name" name="full_name" className={inputCls} placeholder="Your name" autoComplete="name" required {...field('full_name')} />
        {err('full_name')}
      </div>
      <div>
        <label htmlFor="c_email" className="block text-sm font-medium mb-2">Email Address <span className="text-secondary-500">*</span></label>
        <input id="c_email" type="email" name="email" className={inputCls} placeholder="you@example.com" autoComplete="email" required {...field('email')} />
        {err('email')}
      </div>
      <div>
        <label htmlFor="c_phone" className="block text-sm font-medium mb-2">Phone Number {opt}</label>
        <input id="c_phone" type="tel" name="phone" className={inputCls} placeholder="Your phone number" autoComplete="tel" />
      </div>
      <div>
        <label htmlFor="c_service" className="block text-sm font-medium mb-2">Service Type <span className="text-secondary-500">*</span></label>
        <select id="c_service" name="service_type" defaultValue={defaultService} className={`${inputCls} cursor-pointer`} required {...field('service_type')}>
          <option value="" disabled>Select a service</option>
          {contactServiceOptions.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
        </select>
        {err('service_type')}
      </div>
      <div>
        <label htmlFor="c_property" className="block text-sm font-medium mb-2">Property Type {opt}</label>
        <select id="c_property" name="property_type" defaultValue="" className={`${inputCls} cursor-pointer`}>
          <option value="">Select property type</option>
          {propertyTypes.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="c_date" className="block text-sm font-medium mb-2">Preferred Date {opt}</label>
        <input id="c_date" type="date" name="preferred_date" className={`${inputCls} cursor-pointer`} />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="c_details" className="block text-sm font-medium mb-2">Additional Details {opt}</label>
        <textarea id="c_details" name="details" rows={5} maxLength={500} onChange={(ev) => setCount(ev.target.value.length)} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Rooms, pets, areas needing attention…" {...field('details')} />
        <div className="flex justify-between">{err('details')}<span className="ml-auto mt-1.5 text-xs text-foreground-500">{count}/500</span></div>
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
        <button type="submit" disabled={status === 'sending'} className="appearance-none shrink-0 inline-flex items-center justify-center w-full sm:w-auto h-[52px] min-h-[52px] py-4 leading-5 px-8 rounded-full bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 transition-colors">
          {status === 'sending' ? 'Sending…' : 'Request a Quote'}
        </button>
        <div aria-live="polite" className="text-sm">
          {status === 'success' && <p className="text-foreground-950"><i className="ri-checkbox-circle-line text-secondary-500 mr-1" />Thanks — your request was received. We&apos;ll be in touch.</p>}
          {status === 'error' && <p role="alert" className="text-foreground-950"><i className="ri-error-warning-line text-secondary-500 mr-1" />{formError}</p>}
        </div>
      </div>
    </form>
  );
}